import {JsonRpcErrorCodes} from '@microsoft/agent-host-protocol'
import type {ProtectedResourceMetadata, RootState, URI} from '@microsoft/agent-host-protocol'
import {RpcError, type AhpClient} from '@microsoft/agent-host-protocol/client'
import type {AdvertisedEncryptionKey} from './mission-control/mission-control-environment-client'
import {RelayConnectionError} from './relay-failure'

/** localStorage key holding a GitHub token for the AHP `authenticate` handshake. */
export const GITHUB_TOKEN_STORAGE_KEY = 'ahp_github_token'

/** AHP error code returned when the host requires authentication for a protected resource. */
export const AUTH_REQUIRED_CODE = -32007

/** AHP error code indicating that an encryption key is unknown or has the wrong purpose. */
export const ENCRYPTION_KEY_CONFLICT_CODE = -32011

export type EncryptionUse = 'auth-token' | 'mcp-auth-token'

export interface SealedResourceToken {
  token: string
  /** HTTPS-authenticated key the token was sealed to. */
  trustedKey: AdvertisedEncryptionKey
  /** Override when the resource URL alone cannot identify the bearer domain. */
  use?: EncryptionUse
}

export type ResourceToken = string | SealedResourceToken

/** Resolves a bearer for an AHP resource; `undefined` leaves it unauthenticated. */
export interface ResourceTokenProvider {
  (resource: string, scopes?: string[]): Promise<ResourceToken | undefined> | ResourceToken | undefined
  /** Re-mints/reseals after a retryable key conflict. */
  refresh?: (resource: string, scopes?: string[]) => Promise<ResourceToken | undefined> | ResourceToken | undefined
}

interface RootEncryptionKey {
  keyId: string
  use: string
  algorithm: string
  publicKey: string
}

export interface AwpsAuthenticationContext {
  readonly requiredUses: ReadonlySet<EncryptionUse>
  readonly rootKeys: readonly RootEncryptionKey[]
  readonly trustedKeys: readonly AdvertisedEncryptionKey[]
}

interface HandshakeResult {
  _meta?: Record<string, unknown>
}

const REQUIRED_AWPS_USES = ['auth-token', 'mcp-auth-token'] as const
const ENCRYPTION_REQUIRED_META_KEY = 'copilot.encryptionRequired'
const ENCRYPTION_KEYS_META_KEY = 'copilot.encryptionKeys'
const SEALED_PREFIX = 'copilot-sealed.'
const SEALED_V1_PREFIX = 'copilot-sealed.v1.'
const KEBAB_CASE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
const BASE64URL = /^[A-Za-z0-9_-]+$/
const SUPPORTED_ALGORITHMS = new Set(['x25519-sealedbox', 'hpke-x25519-hkdf-sha256-aes256gcm'])

export class AwpsAuthenticationError extends Error {
  readonly retryable: boolean

  constructor(message: string, retryable = false) {
    super(message)
    this.name = 'AwpsAuthenticationError'
    this.retryable = retryable
  }
}

/**
 * Hosts allowed to receive the user's GitHub identity token: the dotcom/GHES
 * allowlist plus the reserved-loopback `github.localhost` (a host-resolved local
 * daemon), which is never routable in production.
 */
function isGitHubHost(host: string): boolean {
  return (
    host === 'github.com' ||
    host.endsWith('.github.com') ||
    host === 'githubcopilot.com' ||
    host.endsWith('.githubcopilot.com') ||
    host.endsWith('.ghe.com') ||
    host === 'github.localhost' ||
    host.endsWith('.github.localhost')
  )
}

/** Hosts whose protected resources may receive the GitHub token. */
export function isGitHubResource(resource: string): boolean {
  const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(resource)
  if (!match) return false
  const authority = match[1] ?? ''
  const host = (authority.split('@').pop() ?? '').split(':')[0]?.toLowerCase() ?? ''
  return isGitHubHost(host)
}

export function defaultResourceTokenProvider(resource: string): string | undefined {
  if (!isGitHubResource(resource)) return undefined
  try {
    return globalThis.localStorage?.getItem(GITHUB_TOKEN_STORAGE_KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function authRequiredResources(data: unknown): ProtectedResourceMetadata[] {
  const resources = (data as {resources?: ProtectedResourceMetadata[]} | undefined)?.resources
  return Array.isArray(resources) ? resources : []
}

/** Compose providers while preserving the provider that supplied a resource for refresh. */
export function withFallbackResourceTokenProvider(
  primary: ResourceTokenProvider,
  fallback: ResourceTokenProvider,
): ResourceTokenProvider {
  const sources = new Map<string, ResourceTokenProvider>()
  const provider: ResourceTokenProvider = async (resource, scopes) => {
    const primaryToken = await (scopes ? primary(resource, scopes) : primary(resource))
    if (primaryToken) {
      sources.set(resource, primary)
      return primaryToken
    }
    const fallbackToken = await (scopes ? fallback(resource, scopes) : fallback(resource))
    if (fallbackToken) sources.set(resource, fallback)
    return fallbackToken
  }
  provider.refresh = async (resource, scopes) => {
    const source = sources.get(resource)
    return scopes ? source?.refresh?.(resource, scopes) : source?.refresh?.(resource)
  }
  return provider
}

/**
 * Validate the raw AWPS initialize/reconnect policy and root key advertisement.
 * This is independent of any relay-carried assertion, so a missing or malformed
 * setup cannot downgrade authentication to plaintext.
 */
export function createAwpsAuthenticationContext(
  result: HandshakeResult,
  snapshots: Array<{resource: URI; state: unknown}> | undefined,
  trustedKeys: ReadonlyArray<AdvertisedEncryptionKey | undefined> = [],
): AwpsAuthenticationContext {
  const rawUses = result._meta?.[ENCRYPTION_REQUIRED_META_KEY]
  if (!Array.isArray(rawUses) || rawUses.length === 0) {
    throw new AwpsAuthenticationError('AWPS handshake is missing a valid copilot.encryptionRequired policy')
  }
  if (
    rawUses.some(use => typeof use !== 'string' || !KEBAB_CASE.test(use)) ||
    new Set(rawUses).size !== rawUses.length
  ) {
    throw new AwpsAuthenticationError('AWPS handshake has a malformed copilot.encryptionRequired policy')
  }
  const requiredUses = new Set(rawUses)
  if (requiredUses.size !== REQUIRED_AWPS_USES.length || REQUIRED_AWPS_USES.some(use => !requiredUses.has(use))) {
    throw new AwpsAuthenticationError('AWPS handshake must require both auth-token and mcp-auth-token encryption')
  }

  const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === 'ahp-root://')
  const rootState = rootSnapshot?.state as RootState | undefined
  const rootKeys = parseRootKeys(rootState?._meta?.[ENCRYPTION_KEYS_META_KEY])
  const presentTrustedKeys = trustedKeys.filter((key): key is AdvertisedEncryptionKey => key !== undefined)
  for (const trustedKey of presentTrustedKeys) {
    assertTrustedKeyMatchesRoot(trustedKey, rootKeys)
  }
  return {
    requiredUses: requiredUses as Set<EncryptionUse>,
    rootKeys,
    trustedKeys: presentTrustedKeys,
  }
}

/** Push tokens for each required resource the provider can satisfy; returns true if any was authenticated. */
export async function satisfyResources(
  client: AhpClient,
  resources: ProtectedResourceMetadata[],
  provider: ResourceTokenProvider,
  awps?: AwpsAuthenticationContext,
): Promise<boolean> {
  let satisfied = false
  for (const meta of resources) {
    if (meta.required === false) continue
    const authenticated = await authenticateResource(client, meta.resource, meta.scopes_supported, provider, awps)
    if (!authenticated) continue
    satisfied = true
  }
  return satisfied
}

async function authenticateResource(
  client: AhpClient,
  resource: string,
  scopes: string[] | undefined,
  provider: ResourceTokenProvider,
  awps: AwpsAuthenticationContext | undefined,
): Promise<boolean> {
  let candidate = await (scopes ? provider(resource, scopes) : provider(resource))
  if (!candidate) return false

  for (let attempt = 0; ; attempt++) {
    try {
      const token = awps ? validateSealedToken(candidate, resource, awps) : tokenString(candidate)
      await client.request('authenticate', {
        channel: 'ahp-root://',
        resource,
        token,
        ...(scopes ? {scopes} : {}),
      })
      return true
    } catch (error) {
      const retryable =
        (error instanceof RpcError && error.code === ENCRYPTION_KEY_CONFLICT_CODE) ||
        (error instanceof AwpsAuthenticationError && error.retryable)
      if (!retryable || attempt > 0 || !provider.refresh) throw classifyAuthenticateError(error)
      candidate = await (scopes ? provider.refresh(resource, scopes) : provider.refresh(resource))
      if (!candidate) throw error
    }
  }
}

// The host answers `authenticate` with -32603 when it could not reach GitHub to
// verify the identity (timeout, 5xx, transport error or rate limit). The
// credential was not refused, so tag the failure to let the runtime retry it.
function classifyAuthenticateError(error: unknown): unknown {
  if (!(error instanceof RpcError) || error.code !== JsonRpcErrorCodes.InternalError) return error
  const data = error.data
  if (data && typeof data === 'object' && 'kind' in data && data.kind === 'cliVersionMismatch') return error
  return new RelayConnectionError(
    error.message,
    {kind: 'rpc', code: error.code, subtype: 'authenticateUnavailable'},
    {cause: error},
  )
}

function tokenString(candidate: ResourceToken): string {
  return typeof candidate === 'string' ? candidate : candidate.token
}

function validateSealedToken(candidate: ResourceToken, resource: string, context: AwpsAuthenticationContext): string {
  const token = tokenString(candidate)
  const use =
    typeof candidate === 'string'
      ? encryptionUseForResource(resource)
      : (candidate.use ?? encryptionUseForResource(resource))
  if (!context.requiredUses.has(use)) {
    throw new AwpsAuthenticationError(`AWPS handshake does not require encryption for ${use}`)
  }
  const keyId = parseSealedKeyId(token)
  const trustedKey =
    typeof candidate === 'string'
      ? context.trustedKeys.find(key => key.key_id === keyId && key.use === use)
      : candidate.trustedKey
  if (!trustedKey) {
    throw new AwpsAuthenticationError(`no HTTPS-trusted ${use} key matches sealed token key '${keyId}'`, true)
  }
  if (trustedKey.key_id !== keyId || trustedKey.use !== use) {
    throw new AwpsAuthenticationError(`sealed token key '${keyId}' is inconsistent with its trusted ${use} key`, true)
  }
  assertTrustedKeyMatchesRoot(trustedKey, context.rootKeys)
  return token
}

function encryptionUseForResource(resource: string): EncryptionUse {
  return isGitHubResource(resource) ? 'auth-token' : 'mcp-auth-token'
}

function parseSealedKeyId(token: string): string {
  if (!token.startsWith(SEALED_PREFIX)) {
    throw new AwpsAuthenticationError('refusing to send a plaintext authenticate bearer over AWPS')
  }
  if (!token.startsWith(SEALED_V1_PREFIX)) {
    throw new AwpsAuthenticationError('refusing an unsupported sealed authenticate envelope version')
  }
  const segments = token.split('.')
  const keyId = segments[2] ?? ''
  const box = segments[3] ?? ''
  if (segments.length !== 4 || !BASE64URL.test(keyId) || !BASE64URL.test(box) || base64UrlByteLength(box) < 48) {
    throw new AwpsAuthenticationError('refusing a malformed sealed authenticate envelope')
  }
  return keyId
}

function base64UrlByteLength(value: string): number {
  const remainder = value.length % 4
  if (remainder === 1) return -1
  return Math.floor((value.length * 6) / 8)
}

function parseRootKeys(value: unknown): RootEncryptionKey[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new AwpsAuthenticationError('AWPS root snapshot is missing copilot.encryptionKeys')
  }
  const keys = value.map((entry, index) => parseRootKey(entry, index))
  if (new Set(keys.map(key => key.keyId)).size !== keys.length) {
    throw new AwpsAuthenticationError('AWPS root snapshot contains duplicate encryption key ids')
  }
  return keys
}

function parseRootKey(value: unknown, index: number): RootEncryptionKey {
  if (!isRecord(value)) {
    throw new AwpsAuthenticationError(`AWPS root encryption key ${index} is not an object`)
  }
  const expectedFields = ['algorithm', 'keyId', 'publicKey', 'use']
  if (Object.keys(value).sort().join(',') !== expectedFields.join(',')) {
    throw new AwpsAuthenticationError(`AWPS root encryption key ${index} has an invalid shape`)
  }
  const key = {
    keyId: value['keyId'],
    use: value['use'],
    algorithm: value['algorithm'],
    publicKey: value['publicKey'],
  }
  if (
    typeof key.keyId !== 'string' ||
    !BASE64URL.test(key.keyId) ||
    typeof key.use !== 'string' ||
    !KEBAB_CASE.test(key.use) ||
    typeof key.algorithm !== 'string' ||
    !SUPPORTED_ALGORITHMS.has(key.algorithm) ||
    typeof key.publicKey !== 'string' ||
    !isThirtyTwoByteBase64(key.publicKey)
  ) {
    throw new AwpsAuthenticationError(`AWPS root encryption key ${index} contains invalid fields`)
  }
  return key as RootEncryptionKey
}

function assertTrustedKeyMatchesRoot(
  trustedKey: AdvertisedEncryptionKey,
  rootKeys: readonly RootEncryptionKey[],
): void {
  if (
    !BASE64URL.test(trustedKey.key_id) ||
    !KEBAB_CASE.test(trustedKey.use) ||
    !SUPPORTED_ALGORITHMS.has(trustedKey.algorithm) ||
    !isThirtyTwoByteBase64(trustedKey.public_key)
  ) {
    throw new AwpsAuthenticationError('Mission Control returned a malformed trusted encryption key')
  }
  const match = rootKeys.find(key => key.keyId === trustedKey.key_id)
  if (
    !match ||
    match.use !== trustedKey.use ||
    match.algorithm !== trustedKey.algorithm ||
    match.publicKey !== trustedKey.public_key
  ) {
    throw new AwpsAuthenticationError(
      `Mission Control trusted key '${trustedKey.key_id}' does not match the AWPS root snapshot`,
      true,
    )
  }
}

function isThirtyTwoByteBase64(value: string): boolean {
  return /^[A-Za-z0-9+/]{43}=$/.test(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
